import { computed, inject, Injectable, signal, DestroyRef, NgZone } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Observable, of, map, concatMap, tap, catchError, timer } from 'rxjs';
import { MatDialogConfig } from '@angular/material/dialog';
import { SecurityApiService } from '../http/security-api.service';
import { AuthorizationService } from '../authorization/authorization.service';
import { SignalRService } from '../services/signalr.service';
import { CloneService } from '../../shared/utilities/clone.service';
import { AppUser, AuthenticatedUser, UserGroupEnum } from '../authorization/models';
import { CredentialToken, WebLogin } from './models';

/**
 * Service for managing user authentication state and operations.
 * Uses signals for reactive state management.
 *
 * Security Architecture:
 * - Access token is stored in memory only (via the currentUser signal)
 * - Refresh token is stored in HttpOnly cookie (set by server)
 * - On page refresh, session is restored via the refresh endpoint
 * - Proactive token refresh before expiration prevents interruption
 */
@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly securityApiService = inject(SecurityApiService);
  private readonly cloneService = inject(CloneService);
  private readonly router = inject(Router);
  private readonly signalRService = inject(SignalRService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngZone = inject(NgZone);

  /** Buffer time before token expiration to trigger refresh (in milliseconds) */
  private readonly TOKEN_REFRESH_BUFFER_MS = 60_000; // 1 minute before expiry

  private readonly _loggedIn = signal<boolean>(false);
  readonly loggedIn = this._loggedIn.asReadonly();

  private readonly _currentUser = signal<AuthenticatedUser | null>(null);
  readonly currentUser = this._currentUser.asReadonly();

  private readonly _sessionInitialized = signal<boolean>(false);
  readonly sessionInitialized = this._sessionInitialized.asReadonly();

  /** Token expiration time in milliseconds since epoch */
  private tokenExpiresAt: number | null = null;

  /**
   * Computed signal for the current user's app user data.
   */
  readonly appUser = computed(() => this._currentUser()?.appUser ?? null);

  /**
   * Computed signal for the current user's display name.
   */
  readonly displayName = computed(() => {
    const user = this._currentUser()?.appUser;
    if (!user) return '';
    return `${user.firstName} ${user.lastName}`.trim();
  });

  /**
   * Sets the current user and manages related state.
   * Token is stored in memory only via the signal.
   * @param user - The authenticated user or null to clear
   * @param expiresIn - Token expiration time in seconds (optional)
   * @returns The user's home route
   */
  private setUser(user: AuthenticatedUser | null, expiresIn?: number): string {
    this._currentUser.set(user);
    this._loggedIn.set(user !== null);

    if (user && expiresIn) {
      this.tokenExpiresAt = Date.now() + expiresIn * 1000;
      this.scheduleTokenRefresh(expiresIn);
    } else {
      this.tokenExpiresAt = null;
    }

    if (!this.isRater()) {
      if (user) {
        // Factory reads the signal so reconnects always present the current token,
        // not the one captured at login (which goes stale after proactive refresh).
        this.signalRService.initiateSignalRConnection(() => this._currentUser()?.token ?? '');
      } else {
        this.signalRService.closeConnection();
      }
    }

    const home = this.authorizationService.setupUser(user);

    return home;
  }

  /**
   * Schedules proactive token refresh before expiration.
   * Uses NgZone.runOutsideAngular to avoid triggering change detection.
   * @param expiresIn - Token expiration time in seconds
   */
  private scheduleTokenRefresh(expiresIn: number): void {
    const refreshTime = expiresIn * 1000 - this.TOKEN_REFRESH_BUFFER_MS;

    if (refreshTime <= 0) {
      // Token is about to expire, refresh immediately
      this.performProactiveRefresh();
      return;
    }

    this.ngZone.runOutsideAngular(() => {
      timer(refreshTime)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(() => {
          this.ngZone.run(() => {
            this.performProactiveRefresh();
          });
        });
    });
  }

  /**
   * Performs proactive token refresh before expiration.
   */
  private performProactiveRefresh(): void {
    this.refreshToken().subscribe({
      error: () => {
        // Refresh failed, user will need to log in again on next API call
        console.warn('Proactive token refresh failed');
      },
    });
  }

  /**
   * Authenticates a user with email and password credentials.
   * @param login - The login credentials
   * @returns Observable tuple of [AuthenticatedUser, home route]
   */
  login(login: WebLogin): Observable<[AuthenticatedUser, string]> {
    return this.initializeUser(this.securityApiService.login(login));
  }

  /**
   * Authenticates a rater using their unique GUID.
   * @param guid - The rater's unique identifier
   * @returns Observable tuple of [AuthenticatedUser, home route]
   */
  raterLogin(guid: string): Observable<[AuthenticatedUser, string]> {
    return this.initializeUser(this.securityApiService.raterLogin(guid));
  }

  /**
   * Initializes the session on app startup.
   * Attempts to restore authentication using the HttpOnly refresh token cookie.
   * @returns Observable that completes when session initialization is done
   */
  initializeSession(): Observable<boolean> {
    return this.securityApiService.refreshSession().pipe(
      concatMap((response) => {
        const user: AuthenticatedUser = {
          appUser: response.user,
          token: response.accessToken,
        };
        return this.authorizationService.initialize().pipe(
          map(() => {
            this.setUser(user, response.expiresIn);
            this._sessionInitialized.set(true);
            return true;
          }),
        );
      }),
      catchError(() => {
        // No valid session - user needs to log in
        this._sessionInitialized.set(true);
        return of(false);
      }),
    );
  }

  /**
   * Initializes auth state for public routes that must not restore a cookie session.
   */
  initializeAnonymousSession(): Observable<boolean> {
    this.setUser(null);
    this._sessionInitialized.set(true);
    return of(false);
  }

  /**
   * Initializes user state after successful authentication.
   */
  private initializeUser(
    login$: Observable<{ user: AuthenticatedUser; expiresIn: number }>,
  ): Observable<[AuthenticatedUser, string]> {
    let userData: { user: AuthenticatedUser; expiresIn: number };
    return login$.pipe(
      concatMap((result) => {
        userData = result;
        return this.authorizationService.initialize().pipe(map(() => userData));
      }),
      map((data) => {
        const home = this.setUser(data.user, data.expiresIn);
        return [data.user, home] as [AuthenticatedUser, string];
      }),
    );
  }

  /**
   * Creates a deep clone of the current user's app user data.
   * @returns A cloned AppUser object or null if not authenticated
   */
  cloneCurrentUser(): AppUser | null {
    const user = this._currentUser();
    if (!user) return null;
    return this.cloneService.deepClone(user.appUser);
  }

  /**
   * Logs out the current user, clears server session, and navigates to the home page.
   * The server will clear the HttpOnly refresh token cookie.
   */
  logout(): void {
    this.securityApiService.logout().subscribe({
      complete: () => {
        this.clearSession();
      },
      error: () => {
        // Clear local session even if server call fails
        this.clearSession();
      },
    });
  }

  /**
   * Clears the local session state and navigates to home.
   */
  private clearSession(): void {
    this.setUser(null);
    this.router.navigate(['']);
  }

  /**
   * Gets the dialog configuration for login modals.
   */
  get loginDialogConfig(): MatDialogConfig {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    return dialogConfig;
  }

  /**
   * Checks if the current user is a rater.
   */
  isRater(): boolean {
    return (
      this._currentUser()?.appUser.userGroups?.some(
        (g) => g.userGroupId === UserGroupEnum.Rater,
      ) ?? false
    );
  }

  /**
   * Checks if the current user is a participant.
   */
  isParticipant(): boolean {
    return (
      this._currentUser()?.appUser.userGroups?.some(
        (g) => g.userGroupId === UserGroupEnum.Participant,
      ) ?? false
    );
  }

  /**
   * Checks if the current user is a client admin.
   */
  isClient(): boolean {
    return (
      this._currentUser()?.appUser.userGroups?.some(
        (g) => g.userGroupId === UserGroupEnum.ClientAdmin,
      ) ?? false
    );
  }

  /**
   * Checks if the current user is a system admin.
   */
  isAdmin(): boolean {
    return (
      this._currentUser()?.appUser.userGroups?.some(
        (g) => g.userGroupId === UserGroupEnum.SysAdmin,
      ) ?? false
    );
  }

  /**
   * Checks if the current user is an account admin.
   */
  isAccountAdmin(): boolean {
    return (
      (this.isAdmin() || this._currentUser()?.appUser.userGroups?.some(
        (g) => g.userGroupId === UserGroupEnum.AccountAdmin,
      )) ?? false
    );
  }

  /**
   * Checks if the current user is a client admin, account admin, or system admin.
   */
  isClientOrAccountAdmin(): boolean {
    return this.isClient() || this.isAccountAdmin();
  }

  /**
   * Checks if the current user is both a participant and has admin privileges.
   */
  isParticipantAndAdmin(): boolean {
    return (this.isAdmin() || this.isClient() || this.isAccountAdmin()) && this.isParticipant();
  }

  /**
   * Refreshes the current user's authentication token.
   * Uses the HttpOnly refresh token cookie for secure refresh.
   * Updates the token in memory via the signal.
   * @returns Observable of the new credential token
   */
  refreshToken(): Observable<CredentialToken> {
    const user = this._currentUser();

    if (!user) {
      this.clearSession();
      return of({
        access_token: '',
        expires_in: 0,
        token_type: 'Bearer',
      });
    }

    // Use cookie-based refresh (no jti needed - cookie is sent automatically)
    return this.securityApiService.refreshToken().pipe(
      tap((result) => {
        const updatedUser: AuthenticatedUser = {
          ...user,
          token: result.access_token,
        };
        this._currentUser.set(updatedUser);

        // Schedule next refresh
        if (result.expires_in > 0) {
          this.tokenExpiresAt = Date.now() + result.expires_in * 1000;
          this.scheduleTokenRefresh(result.expires_in);
        }
      }),
    );
  }

  /**
   * Gets the access token for the current user.
   * @returns The access token or null if not authenticated
   */
  getAccessToken(): string | null {
    return this._currentUser()?.token ?? null;
  }

  /**
   * Checks if the current token is expired or about to expire.
   * @returns True if token needs refresh
   */
  isTokenExpired(): boolean {
    if (!this.tokenExpiresAt) {
      return true;
    }
    return Date.now() >= this.tokenExpiresAt - this.TOKEN_REFRESH_BUFFER_MS;
  }
}
